// src/pages/admin/AdminUsers.js
// Superadmin: every user by status, with search, paging and per-user actions.
// Registrations are approved per organization (AdminOrganizations.js).
// ?status= picks the tab and ?user=<id> opens that user's details.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../../utils/api';
import { StatusBadge, UserDetailModal, ActionDialog, ACTIONS, errorOf, fmtDay, fmtDate } from './common';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'deactivated', label: 'Deactivated' },
];
const LIMIT = 20;

export default function AdminUsers() {
  const [params, setParams] = useSearchParams();
  const status = TABS.some(t => t.id === params.get('status')) ? params.get('status') : 'all';
  const openUserId = params.get('user');

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('created_at:desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(null); // { user, action } from a row button
  const [reloadKey, setReloadKey] = useState(0);

  // Debounce typing into the search box
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const [sortBy, order] = sort.split(':');
    let cancelled = false;
    setData(d => (d ? { ...d, loading: true } : d));
    adminAPI.users({ status, q: q || undefined, sort: sortBy, order, page, limit: LIMIT })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => toast.error(errorOf(err, 'Could not load users')));
    return () => { cancelled = true; };
  }, [status, q, sort, page, reloadKey]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key === 'user' });
  };
  const changeTab = (id) => { setPage(1); setParam('status', id); };
  const reload = () => setReloadKey(k => k + 1);

  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const first = data?.total ? (page - 1) * LIMIT + 1 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">Individual people. Approve new sign-ups on the Organizations page.</div>
        </div>
      </div>
      <div style={{ display: 'flex', padding: '0 16px', background: '#fff', borderBottom: '1px solid #e8eaf0', flexShrink: 0, overflowX: 'auto' }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => changeTab(t.id)}
            style={{ padding: '11px 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', color: status === t.id ? '#f4a118' : '#8892a4',
              borderBottom: `2px solid ${status === t.id ? '#f4a118' : 'transparent'}`, fontWeight: status === t.id ? 600 : 500 }}>
            {t.label}
          </div>
        ))}
      </div>
      <div className="toolbar">
        <div className="search-box">
          <span style={{ color: '#aaa' }}>🔍</span>
          <input placeholder="Search name, email or organization" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
        </div>
        <select className="form-select" style={{ width: 'auto', padding: '6px 8px', fontSize: 12 }} value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="last_login_at:desc">Recently signed in</option>
          <option value="name:asc">Name A–Z</option>
          <option value="email:asc">Email A–Z</option>
        </select>
        <span className="count-text ml-auto">{data ? `${first}–${first ? first + data.users.length - 1 : 0} of ${data.total}` : ''}</span>
        <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
        <button className="btn btn-outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!data ? <div className="loading-center"><div className="spinner" /></div> :
          data.users.length === 0 ? (
            <div className="empty-state">
              <span style={{ fontSize: 40 }}>👤</span>
              <h3>No users here</h3>
              <p>{q ? 'Nothing matches your search.' : 'No users with this status.'}</p>
            </div>
          ) : (
            <table className="data-table" style={{ opacity: data.loading ? 0.6 : 1, minWidth: 760 }}>
              <thead>
                <tr><th style={{ width: 'auto' }}>User</th><th>Status</th><th>Registered</th><th>Last sign-in</th><th>Organization</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => setParam('user', u.id)}>
                    <td style={{ width: 'auto' }}>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      <div style={{ color: '#8892a4', fontSize: 11 }}>{u.email}</div>
                    </td>
                    <td><StatusBadge user={u} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(u.created_at)}</td>
                    <td style={{ whiteSpace: 'nowrap', color: u.last_login_at ? undefined : '#aaa' }}>{u.last_login_at ? fmtDate(u.last_login_at) : 'Never'}</td>
                    <td>
                      <div>{u.organization_name || '—'}</div>
                      <div style={{ color: '#8892a4', fontSize: 11, textTransform: 'capitalize' }}>
                        {u.org_role}{u.organization_status && u.organization_status !== 'active' ? ` · organization ${u.organization_status}` : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {u.status === 'pending' && <>
                        <button className="btn btn-primary" style={{ padding: '4px 10px' }} onClick={() => setQuick({ user: u, action: 'approve' })}>Approve</button>{' '}
                        <button className="btn btn-danger" style={{ padding: '4px 10px' }} onClick={() => setQuick({ user: u, action: 'reject' })}>Reject</button>{' '}
                      </>}
                      <button className="btn btn-outline" style={{ padding: '4px 10px' }} onClick={() => setParam('user', u.id)}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {openUserId && <UserDetailModal userId={openUserId} onClose={() => setParam('user', null)} onChanged={reload} />}
      {quick && ACTIONS[quick.action] && (
        <ActionDialog user={quick.user} action={quick.action} onClose={() => setQuick(null)} onDone={() => { setQuick(null); reload(); }} />
      )}
    </div>
  );
}
